import { useState, useEffect, useCallback } from "react";
import web3 from "../App/Service/web3";
import { networkID } from "./utils";

/**
 * LocalStorage keys for saving connected wallet address across page refreshes
 */
const STORAGE_KEYS = ["connectedAccount", "autobet_connected_account", "walletAddress"];

/**
 * Synchronously retrieves last known connected wallet address from localStorage.
 */
export const getSavedAccount = () => {
  if (typeof window === "undefined") return "";
  try {
    for (const key of STORAGE_KEYS) {
      const val = localStorage.getItem(key);
      if (val && typeof val === "string" && val.startsWith("0x")) {
        return val;
      }
    }
  } catch {}
  return "";
};

/**
 * Persists or clears connected wallet address in localStorage.
 */
export const saveConnectedAccount = (account) => {
  if (typeof window === "undefined") return;
  try {
    if (account && typeof account === "string" && account.startsWith("0x")) {
      for (const key of STORAGE_KEYS) {
        localStorage.setItem(key, account);
      }
    } else {
      for (const key of STORAGE_KEYS) {
        localStorage.removeItem(key);
      }
    }
  } catch (err) {
    console.warn("[walletUtils] Could not update localStorage account:", err);
  }
};

/**
 * Synchronizes web3 instance with active Ethereum provider.
 */
const syncWeb3Provider = (provider) => {
  if (typeof window !== "undefined" && provider) {
    try {
      if (web3 && web3.setProvider) {
        web3.setProvider(provider);
      }
      window.web3 = web3;
    } catch (e) {
      console.warn("[walletUtils] Could not sync web3 provider:", e);
    }
  }
};

/**
 * Resolves the injected Ethereum provider (MetaMask, Rabby, Coinbase, etc.).
 * Handles the injection timing race condition on page reload / new tabs.
 */
export const getProvider = async (timeoutMs = 1500) => {
  if (typeof window === "undefined") return null;
  if (window.ethereum) {
    syncWeb3Provider(window.ethereum);
    return window.ethereum;
  }

  return new Promise((resolve) => {
    let resolved = false;

    const handleInitialized = () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        syncWeb3Provider(window.ethereum);
        resolve(window.ethereum || null);
      }
    };

    window.addEventListener("ethereum#initialized", handleInitialized, {
      once: true,
    });

    // Polling fallback every 50ms in case 'ethereum#initialized' is missed or not fired
    const intervalId = setInterval(() => {
      if (window.ethereum && !resolved) {
        resolved = true;
        cleanup();
        syncWeb3Provider(window.ethereum);
        resolve(window.ethereum);
      }
    }, 50);

    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        syncWeb3Provider(window.ethereum);
        resolve(window.ethereum || null);
      }
    }, timeoutMs);

    const cleanup = () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      window.removeEventListener("ethereum#initialized", handleInitialized);
    };
  });
};

/**
 * Returns true if MetaMask / Web3 ethereum provider is injected.
 */
export const isWalletInstalled = () => {
  return typeof window !== "undefined" && Boolean(window.ethereum);
};

/**
 * Non-intrusively queries the currently connected and unlocked account.
 * Handles page reloads and new tabs reliably without false negatives.
 * Standard EIP-1102 / EIP-1193 eth_accounts returns [] when locked/disconnected,
 * and ['0x...'] when unlocked and authorized.
 */
export const getConnectedAccount = async () => {
  const provider = await getProvider();
  if (!provider) {
    return getSavedAccount();
  }

  try {
    syncWeb3Provider(provider);

    const accounts = await provider.request({ method: "eth_accounts" });
    if (accounts && accounts.length > 0 && accounts[0]) {
      saveConnectedAccount(accounts[0]);
      return accounts[0];
    } else {
      // If provider was reachable and explicitly returned 0 accounts, user is disconnected/locked
      saveConnectedAccount("");
      return "";
    }
  } catch (err) {
    console.warn("[walletUtils] Error retrieving connected account:", err);
    return getSavedAccount();
  }
};

/**
 * Explicitly triggers the MetaMask extension popup to connect / unlock accounts.
 * Returns { account, error, isCancelled, isPending, message }
 */
export const connectWallet = async () => {
  const provider = await getProvider();
  if (!provider) {
    return {
      account: "",
      error: "NO_WALLET",
      message:
        "Please install an Ethereum-compatible browser or extension like MetaMask to use this dApp!",
    };
  }

  try {
    syncWeb3Provider(provider);

    const accounts = await provider.request({
      method: "eth_requestAccounts",
    });

    if (accounts && accounts.length > 0 && accounts[0]) {
      saveConnectedAccount(accounts[0]);
      return {
        account: accounts[0],
        error: null,
      };
    }
    return {
      account: "",
      error: "NO_ACCOUNTS",
    };
  } catch (err) {
    if (
      err &&
      (err.code === 4001 ||
        (err.message && err.message.includes("User rejected")))
    ) {
      return {
        account: "",
        error: "USER_REJECTED",
        isCancelled: true,
      };
    }
    if (err && err.code === -32002) {
      return {
        account: "",
        error: "PENDING_REQUEST",
        isPending: true,
        message:
          "MetaMask connection request is already pending. Please open your MetaMask extension popup.",
      };
    }
    return {
      account: "",
      error: "CONNECTION_FAILED",
      message: (err && err.message) || "Failed to connect wallet.",
    };
  }
};

/**
 * Checks if active network ID matches expected network ID.
 */
export const checkNetwork = async (expectedNetworkId = networkID) => {
  const provider = await getProvider();
  if (!provider) {
    return { isCorrect: false, currentNetworkId: null };
  }

  try {
    let currentId = provider.networkVersion;
    if (!currentId && provider.chainId) {
      currentId = parseInt(provider.chainId, 16);
    }
    if (!currentId && web3 && web3.eth && web3.eth.net) {
      currentId = await web3.eth.net.getId();
    }

    const isCorrect = Number(currentId) === Number(expectedNetworkId);
    return {
      isCorrect,
      currentNetworkId: currentId,
    };
  } catch (err) {
    console.warn("[walletUtils] Error checking network ID:", err);
    return { isCorrect: false, currentNetworkId: null };
  }
};

/**
 * Subscribes to window.ethereum accountsChanged, chainChanged, and disconnect events.
 * Automatically resolves provider if not yet ready upon registration.
 * Returns an unsubscribe cleanup function.
 */
export const subscribeToWalletEvents = ({
  onAccountsChanged,
  onChainChanged,
  onDisconnect,
}) => {
  let isCleanedUp = false;
  let activeProvider = null;

  const handleAccountsChanged = (accounts) => {
    if (isCleanedUp) return;
    const account = accounts && accounts.length > 0 ? accounts[0] : "";
    saveConnectedAccount(account);
    if (onAccountsChanged) {
      onAccountsChanged(account, accounts);
    }
  };

  const handleChainChanged = (chainId) => {
    if (isCleanedUp) return;
    if (onChainChanged) {
      onChainChanged(chainId);
    }
  };

  const handleDisconnect = (error) => {
    if (isCleanedUp) return;
    saveConnectedAccount("");
    if (onDisconnect) {
      onDisconnect(error);
    }
  };

  getProvider().then((provider) => {
    if (isCleanedUp || !provider) return;
    activeProvider = provider;
    syncWeb3Provider(provider);

    if (provider.on) {
      provider.on("accountsChanged", handleAccountsChanged);
      provider.on("chainChanged", handleChainChanged);
      provider.on("disconnect", handleDisconnect);
    }
  });

  return () => {
    isCleanedUp = true;
    if (activeProvider && activeProvider.removeListener) {
      activeProvider.removeListener("accountsChanged", handleAccountsChanged);
      activeProvider.removeListener("chainChanged", handleChainChanged);
      activeProvider.removeListener("disconnect", handleDisconnect);
    }
  };
};

/**
 * React Hook for seamless wallet state management.
 */
export const useWallet = () => {
  const [account, setAccount] = useState(() => getSavedAccount());
  const [isConnecting, setIsConnecting] = useState(false);
  const [networkValid, setNetworkValid] = useState(true);

  const refreshAccount = useCallback(async () => {
    const acc = await getConnectedAccount();
    setAccount(acc);
    const netCheck = await checkNetwork();
    setNetworkValid(netCheck.isCorrect);
  }, []);

  useEffect(() => {
    refreshAccount();
    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (acc) => setAccount(acc),
      onChainChanged: () => refreshAccount(),
      onDisconnect: () => setAccount(""),
    });
    return unsubscribe;
  }, [refreshAccount]);

  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    try {
      const res = await connectWallet();
      if (res.account) {
        setAccount(res.account);
        const netCheck = await checkNetwork();
        setNetworkValid(netCheck.isCorrect);
      }
      return res;
    } finally {
      setIsConnecting(false);
    }
  }, []);

  return {
    account,
    setAccount,
    isConnecting,
    networkValid,
    connect: handleConnect,
    refresh: refreshAccount,
  };
};
